import React, { useState } from "react";
import { Globe, Check, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useLanguage } from "@/contexts/LanguageContext";
import { SupportedLanguage } from "@/hooks/useUserLanguage";
import { cn } from "@/lib/utils";

interface LanguageSwitcherProps {
  variant?: "compact" | "full";
  className?: string;
}

const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({
  variant = "compact",
  className,
}) => {
  const { language, setLanguage, languages, currentLanguageInfo, isLoading, isMultilingual } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);

  const handleSelectLanguage = async (lang: SupportedLanguage) => {
    try {
      await setLanguage(lang);
      setIsOpen(false);
    } catch (err) {
      console.error("Error changing language:", err);
    }
  };

  // Hide language switcher on public (non-multilingual) routes
  if (!isMultilingual) {
    return null;
  }

  if (isLoading) {
    return (
      <Button variant="ghost" size="sm" disabled className={className}>
        <Globe className="w-4 h-4 animate-pulse" />
      </Button>
    );
  }

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={cn(
            "gap-1.5 px-2 hover:bg-accent",
            className
          )}
        >
          <span className="text-lg" role="img" aria-label={currentLanguageInfo.name}>
            {currentLanguageInfo.flag}
          </span>
          {variant === "full" && (
            <span className="text-sm font-medium">
              {currentLanguageInfo.code.toUpperCase()}
            </span>
          )}
          <ChevronDown className="w-3 h-3 text-muted-foreground" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        {languages.map((lang) => (
          <DropdownMenuItem
            key={lang.code}
            onClick={() => handleSelectLanguage(lang.code)}
            className={cn(
              "flex items-center justify-between cursor-pointer",
              language === lang.code && "bg-accent"
            )}
          >
            <div className="flex items-center gap-2">
              <span className="text-lg" role="img" aria-label={lang.name}>
                {lang.flag}
              </span>
              <div className="flex flex-col">
                <span className="text-sm font-medium">{lang.nativeName}</span>
                <span className="text-xs text-muted-foreground">{lang.name}</span>
              </div>
            </div>
            {language === lang.code && (
              <Check className="w-4 h-4 text-primary" />
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default LanguageSwitcher;
